/**
 * Lean scope-driven Brand Equity calculation hook (SYR-68, Phase E).
 *
 * Runs the (cached) calculate for the active brand + global MarketerScope and
 * returns the raw response. Used by the destination pages (Detail / Responses /
 * Benchmarks) that need the BE numbers without the Briefing AI narrative.
 *
 * The scope payload sets skipAiAnalysis: true, and the server strips aiSummary
 * from any result fetched that way — so a caller that renders element AI
 * summaries ("First Thoughts") sees nothing, whatever the response count. Pass
 * withAiSummaries to run a second pass that asks for them, mirroring what
 * useBriefingData does: headline numbers land immediately from the fast call,
 * summaries replace it when they arrive. It is opt-in because the second call
 * is a real element-analysis LLM pass on first run for each brand/scope, and
 * Responses/Benchmarks display no summaries.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useCalculateBrandEquityMutation } from '../../services/nestjs-base-api';
import type { IBrandEquityScoreResponse } from '../../services/nestjs-base-api';
import { useMarketerScope } from './scope/MarketerScopeContext';

interface UseScopedBrandEquityOptions {
  /** Also fetch element AI summaries (a second, slower request). */
  withAiSummaries?: boolean;
}

interface UseScopedBrandEquityResult {
  response: IBrandEquityScoreResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** True while the AI pass is still in flight (the numbers are already up). */
  isAiLoading: boolean;
}

export function useScopedBrandEquity(
  options?: UseScopedBrandEquityOptions,
): UseScopedBrandEquityResult {
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { payload } = useMarketerScope();
  const withAiSummaries = !!options?.withAiSummaries;

  const [calculate] = useCalculateBrandEquityMutation();
  const [response, setResponse] = useState<IBrandEquityScoreResponse | undefined>();
  const [aiResponse, setAiResponse] = useState<IBrandEquityScoreResponse | undefined>();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    if (!payload || !jwtToken || !organisationId) return;
    setLoading(true);
    setErr(null);
    calculate({ jwtToken, organisationId, payload })
      .unwrap()
      .then((res) => {
        if (!cancelled) setResponse(res);
      })
      .catch((e) => {
        if (!cancelled) setErr(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payload, jwtToken, organisationId, calculate]);

  // Second pass for the AI summaries. Waits for the fast pass so the numbers
  // are never held up, and skips thin data entirely — no point paying for
  // element analysis when there is no usable score to summarise.
  useEffect(() => {
    let cancelled = false;
    setAiResponse(undefined);
    if (
      !withAiSummaries ||
      !payload ||
      !jwtToken ||
      !organisationId ||
      !response ||
      response.insufficientSampleSize
    ) {
      setAiLoading(false);
      return;
    }
    setAiLoading(true);
    calculate({
      jwtToken,
      organisationId,
      payload: { ...payload, skipAiAnalysis: false },
    })
      .unwrap()
      .then((res) => {
        if (!cancelled) setAiResponse(res);
      })
      .catch(() => {
        /* Best-effort: the panel keeps its no-summary fallback. */
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    withAiSummaries,
    payload,
    jwtToken,
    organisationId,
    calculate,
    response,
  ]);

  return {
    // Prefer the summary-bearing result once it lands; identical numbers, so
    // swapping it in mid-render only adds the AI text.
    response: aiResponse ?? response,
    isLoading: loading,
    isError: Boolean(err),
    error: err,
    isAiLoading: aiLoading,
  };
}
